import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { payWithRazorpay } from "@/lib/razorpay";
import { api, API, formatErr, inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Download, Pencil, XCircle } from "lucide-react";
import { ModifyDialog, CancelDialog } from "@/components/ManageBooking";
import { toast } from "sonner";

export default function BookingLookup() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [online, setOnline] = useState(false);
  const [paying, setPaying] = useState(false);
  useEffect(() => { api.get("/").then((r) => setOnline(r.data.payment_mode === "live")).catch(() => {}); }, []);
  const [mobile, setMobile] = useState("");
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState("");
  const auth = { booking_ref: ref, mobile };
  const refresh = () => api.post("/bookings/lookup", auth).then((r) => setB(r.data));

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/bookings/manage/pay", auth);
      payWithRazorpay(data, () => refresh());
    } catch (err) { toast.error(formatErr(err)); } finally { setPaying(false); }
  };

  const search = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/lookup", { booking_ref: ref, mobile });
      setB(data);
    } catch (err) {
      setB(null);
      toast.error(formatErr(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="jt-container py-14 max-w-2xl">
      <div className="jt-eyebrow mb-3">Find my booking</div>
      <h1 className="font-serif text-4xl text-[#1E3A2B] mb-6">Enter your details</h1>
      <form onSubmit={search} className="bg-white rounded-2xl p-6 ring-1 ring-black/5 grid gap-4" data-testid="lookup-form">
        <div>
          <label className="jt-eyebrow mb-2 block">Booking reference</label>
          <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="JT-ABCD1234" data-testid="lookup-ref"/>
        </div>
        <div>
          <label className="jt-eyebrow mb-2 block">Registered mobile</label>
          <Input value={mobile} onChange={(e) => setMobile(e.target.value)} data-testid="lookup-mobile"/>
        </div>
        <button className="jt-pill-primary justify-self-start" disabled={loading} data-testid="lookup-submit">
          {loading ? "Searching…" : "Find booking"}
        </button>
      </form>

      {b && (
        <div className="mt-8 bg-white ring-1 ring-black/5 rounded-2xl p-6" data-testid="lookup-result">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <div className="jt-eyebrow">Reference</div>
              <div className="font-mono text-xl text-[#1E3A2B]">{b.booking_ref}</div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${b.status === "confirmed" ? "bg-[#1E3A2B] text-white" : "bg-[#F5F0EB] text-[#5C6B63]"}`}>{b.status}</span>
          </div>
          <dl className="grid grid-cols-2 gap-y-2 text-sm" data-testid="lookup-summary">
            {(b.summary || [
              ["Safari", b.safari_name],
              ["Date", `${b.slot_date} · ${b.slot_time}`],
              ["Type", b.booking_type],
              ["Guests", `${b.adults} adults / ${b.children} kids`],
            ]).map(([k, v]) => (
              <div key={k} className="contents"><dt className="text-[#5C6B63]">{k}</dt><dd className="font-medium">{v}</dd></div>
            ))}
            <dt className="text-[#5C6B63]">Lead guest</dt><dd className="font-medium">{b.guest_name}</dd>
            {b.pickup_location && (<><dt className="text-[#5C6B63]">Pickup</dt><dd className="font-medium">{b.pickup_location}</dd></>)}
            <dt className="text-[#5C6B63]">Amount</dt><dd className="font-serif text-lg text-[#1E3A2B]">{inr(b.amount)}</dd>
            <dt className="text-[#5C6B63]">Payment</dt><dd className="font-medium">{b.payment?.status}</dd>
          </dl>
          {b.refunds?.length > 0 && (
            <div className="mt-4 text-sm bg-[#F5F0EB] rounded-xl p-3" data-testid="lookup-refunds">
              {b.refunds.map((r, i) => <div key={i} className="flex justify-between"><span>Refund · {r.reason}</span><span>{inr(r.amount)} · {r.status}</span></div>)}
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-6">
            {b.status === "pending" && online && (
              <button onClick={payNow} disabled={paying} className="jt-pill-accent text-sm disabled:opacity-50" data-testid="lookup-pay-btn">
                {paying ? "Opening payment…" : `Pay ${inr(b.amount)} now`}
              </button>
            )}
            {b.status === "pending" && b.hold_expires_at && (
              <p className="w-full text-xs text-[#5C6B63]" data-testid="lookup-hold-note">Held until {new Date(b.hold_expires_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" })} IST. Unpaid holds are released automatically.</p>
            )}
            {b.status === "confirmed" && b.voucher_token && (
              <a href={`${API}/vouchers/${b.voucher_token}`} className="jt-pill-accent text-sm" data-testid="lookup-voucher-btn">
                <Download size={15}/> Download voucher
              </a>
            )}
            {b.manage?.can_modify && (
              <button onClick={() => setDlg("modify")} className="jt-pill-outline text-sm" data-testid="lookup-modify-btn"><Pencil size={14}/> Change booking</button>
            )}
            {b.manage?.can_cancel && (
              <button onClick={() => setDlg("cancel")} className="jt-pill text-sm border-2 border-[#A33A2A] text-[#A33A2A] hover:bg-[#A33A2A] hover:text-white" data-testid="lookup-cancel-btn"><XCircle size={14}/> Cancel booking</button>
            )}
          </div>
          {b.status === "confirmed" && !b.manage?.can_modify && b.manage?.can_cancel && (
            <p className="text-xs text-[#5C6B63] mt-3" data-testid="lookup-change-cutoff">Online changes close {b.manage.change_cutoff_hours} hours before start — call or WhatsApp +91 93753 32211 for help.</p>
          )}
          <ModifyDialog booking={b} open={dlg === "modify"} onOpenChange={(o) => !o && setDlg("")}
            note={`Changes to the same or a lower price can be made online up to ${b.manage?.change_cutoff_hours || 48} hours before start. Any difference is refunded.`}
            quoteFn={(ch) => api.post("/bookings/manage/modify-quote", { ...auth, ...ch })}
            submitFn={(ch) => api.post("/bookings/manage/modify", { ...auth, ...ch })}
            onDone={refresh}/>
          <CancelDialog booking={b} open={dlg === "cancel"} onOpenChange={(o) => !o && setDlg("")}
            previewFn={() => api.post("/bookings/manage/cancel-preview", auth)}
            submitFn={(body) => api.post("/bookings/manage/cancel", { ...auth, ...body })}
            onDone={refresh}/>
        </div>
      )}
    </div>
  );
}
